import type { Modelo } from '@/features/documentos/rotulos';

/**
 * Miniatura de um dos cinco papéis.
 *
 * ⚠️ **HTML e CSS, não SVG nem imagem.** Dois motivos:
 *
 * 1. A cor de destaque entra por `style={{ background: cor }}`, então a
 *    miniatura muda AO VIVO quando o médico mexe no seletor de cor — que é
 *    metade do valor de ter miniatura. Um SVG estático precisaria ser
 *    reescrito por cor.
 * 2. Herda `bg-papel`, a paleta fixa que `PreviaDaAssinatura` estabeleceu: o
 *    documento sai em papel branco, e a miniatura tem de sair igual nos dois
 *    temas. Com `bg-muted`, no tema escuro ela pareceria um papel preto.
 *
 * As proporções não são as do A4 de verdade: é um retrato reconhecível, não uma
 * renderização. A prova visual é o botão "Prévia em PDF".
 */
export function MiniaturaDeLayout({ modelo, cor }: { modelo: Modelo; cor: string }) {
  return (
    <div
      aria-hidden
      className="aspect-[3/4] w-full rounded-sm bg-papel overflow-hidden border border-papel-borda"
    >
      {modelo === 'FAIXA' && <Faixa cor={cor} />}
      {modelo === 'MOLDURA' && <Moldura cor={cor} />}
      {modelo === 'MINIMALISTA' && <Minimalista cor={cor} />}
      {modelo === 'COMPACTO' && <Compacto cor={cor} />}
      {modelo === 'CLASSICO' && <Classico cor={cor} />}
    </div>
  );
}

/** Barra de texto falso. `w` em porcentagem para acompanhar a largura do card. */
function Linha({ w = '100%', escura = false }: { w?: string; escura?: boolean }) {
  return (
    <div
      className={escura ? 'h-[2px] rounded-full bg-papel-texto/50' : 'h-[2px] rounded-full bg-papel-suave/40'}
      style={{ width: w }}
    />
  );
}

function Titulo({ cor, largura = '55%' }: { cor: string; largura?: string }) {
  return <div className="h-[3px] rounded-full" style={{ background: cor, width: largura }} />;
}

function Classico({ cor }: { cor: string }) {
  return (
    <div className="h-full p-1.5 flex flex-col gap-1">
      <div className="flex items-start justify-between">
        <div className="h-2 w-5 rounded-[1px]" style={{ background: cor }} />
        <div className="flex flex-col items-end gap-[2px]">
          <Linha w="14px" />
          <Linha w="10px" />
        </div>
      </div>
      <div className="border-b" style={{ borderColor: `${cor}33` }} />
      <div className="flex justify-center">
        <Titulo cor={cor} largura="50%" />
      </div>
      <div className="rounded-[2px] border p-1 flex flex-col gap-[3px]" style={{ borderColor: `${cor}33` }}>
        <Linha w="90%" />
        <Linha w="70%" />
      </div>
      <div className="flex flex-col gap-[3px] mt-auto">
        <Linha w="85%" escura />
        <Linha w="60%" />
      </div>
    </div>
  );
}

function Faixa({ cor }: { cor: string }) {
  return (
    <div className="h-full flex flex-col">
      {/* Sangra até a borda — é o que define o modelo. */}
      <div className="h-5 px-1.5 flex flex-col justify-center gap-[2px]" style={{ background: cor }}>
        <div className="h-[3px] w-8 rounded-full bg-white/90" />
        <div className="h-[2px] w-12 rounded-full bg-white/60" />
      </div>
      <div className="p-1.5 flex flex-col gap-1 flex-1">
        <Titulo cor={cor} largura="45%" />
        <div className="flex flex-col">
          <div className="h-[5px]" style={{ background: `${cor}14` }} />
          <div className="h-[5px]" />
          <div className="h-[5px]" style={{ background: `${cor}14` }} />
        </div>
        <div className="flex flex-col gap-[3px] mt-auto">
          <Linha w="80%" escura />
          <Linha w="55%" />
        </div>
      </div>
    </div>
  );
}

function Moldura({ cor }: { cor: string }) {
  return (
    <div className="h-full p-[3px]">
      <div className="h-full rounded-[2px] border-[1.5px] p-1.5 flex flex-col gap-1" style={{ borderColor: cor }}>
        <div className="flex flex-col items-center gap-[2px]">
          <div className="h-2 w-5 rounded-[1px]" style={{ background: cor }} />
          <Linha w="60%" />
          <Linha w="40%" />
        </div>
        <div className="border-y py-[2px] flex justify-center" style={{ borderColor: `${cor}33` }}>
          <Titulo cor={cor} largura="50%" />
        </div>
        <div className="rounded-[2px] border p-1 flex flex-col gap-[3px]" style={{ borderColor: `${cor}33` }}>
          <Linha w="90%" />
          <Linha w="75%" />
        </div>
        <div className="mt-auto flex justify-center">
          <Linha w="55%" escura />
        </div>
      </div>
    </div>
  );
}

function Minimalista({ cor }: { cor: string }) {
  return (
    <div className="h-full p-2 flex flex-col gap-2">
      <Linha w="70%" />
      <div className="flex items-center gap-1">
        {/* A régua lateral é a única cor do modelo. */}
        <div className="w-[2px] h-[5px] rounded-full" style={{ background: cor }} />
        <Linha w="35%" escura />
      </div>
      <Linha w="88%" />
      <div className="flex items-center gap-1">
        <div className="w-[2px] h-[5px] rounded-full" style={{ background: cor }} />
        <Linha w="30%" escura />
      </div>
      <Linha w="80%" />
      <div className="mt-auto flex justify-center">
        <Linha w="50%" escura />
      </div>
    </div>
  );
}

function Compacto({ cor }: { cor: string }) {
  return (
    <div className="h-full p-1 flex flex-col gap-[3px]">
      <div className="flex items-start justify-between">
        <div className="h-[6px] w-4 rounded-[1px]" style={{ background: cor }} />
        <div className="flex flex-col items-end gap-[1px]">
          <Linha w="12px" />
          <Linha w="9px" />
          <Linha w="11px" />
        </div>
      </div>
      {/* Título em chip preenchido. */}
      <div className="h-[5px] w-[38%] rounded-[1px]" style={{ background: cor }} />
      <div className="grid grid-cols-4 gap-[1px]">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="h-[6px] border" style={{ borderColor: `${cor}33` }} />
        ))}
      </div>
      <div className="flex flex-col gap-[2px]">
        <Linha w="95%" />
        <Linha w="92%" />
        <Linha w="88%" />
        <Linha w="94%" />
      </div>
      <div className="mt-auto flex justify-center">
        <Linha w="45%" escura />
      </div>
    </div>
  );
}
